(function () {
  'use strict';

  var DB_NAME = 'trc-background-uploads';
  var DB_VERSION = 1;
  var JOB_STORE = 'jobs';
  var FILE_STORE = 'files';
  var WORKER_URL = 'https://uploads.tablereadycards.com';
  var ACTIVE_STATUSES = ['queued', 'uploading', 'finalizing', 'adding'];
  var databasePromise = null;
  var processing = false;
  var processingRequested = false;
  var attemptedThisRun = Object.create(null);
  var sessionLocks = Object.create(null);
  var tokenProvider = null;
  var stopped = false;
  var internalNavigationUntil = 0;
  var autoStart = window.__TRC_BACKGROUND_UPLOADS_AUTOSTART__ !== false;
  var updateChannel = null;
  try { updateChannel = new BroadcastChannel('trc-background-uploads'); } catch (_) {}

  function requestPromise(request) {
    return new Promise(function (resolve, reject) {
      request.onsuccess = function () { resolve(request.result); };
      request.onerror = function () { reject(request.error || new Error('Browser storage failed.')); };
    });
  }

  function transactionPromise(transaction) {
    return new Promise(function (resolve, reject) {
      transaction.oncomplete = function () { resolve(); };
      transaction.onerror = function (event) { reject(event.target.error || transaction.error || new Error('Browser storage failed.')); };
      transaction.onabort = function () { reject(transaction.error || new Error('Browser storage was interrupted.')); };
    });
  }

  // The open connection. Safari can drop it (a storage process crash, "Connection
  // to Indexed Database server lost") and another tab can ask it to close for
  // a version change; either way the next operation opens a fresh one.
  var currentDatabase = null;

  function forgetDatabase(database) {
    if (database && currentDatabase !== database) return;
    currentDatabase = null;
    databasePromise = null;
  }

  function storageUnavailableError(cause) {
    var error = new Error('This browser cannot keep uploads running between pages.');
    error.code = 'UPLOAD_STORAGE_UNAVAILABLE';
    if (cause) error.cause = cause;
    return error;
  }

  function openDatabase() {
    if (databasePromise) return databasePromise;
    databasePromise = new Promise(function (resolve, reject) {
      var factory = null;
      // Reading indexedDB itself throws when site storage is blocked.
      try { factory = window.indexedDB; } catch (error) { reject(storageUnavailableError(error)); return; }
      if (!factory || typeof factory.open !== 'function') {
        reject(storageUnavailableError());
        return;
      }
      var request = factory.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = function () {
        var database = request.result;
        if (!database.objectStoreNames.contains(JOB_STORE)) {
          database.createObjectStore(JOB_STORE, { keyPath: 'id' });
        }
        if (!database.objectStoreNames.contains(FILE_STORE)) {
          var files = database.createObjectStore(FILE_STORE, { keyPath: 'key' });
          files.createIndex('jobId', 'jobId', { unique: false });
        }
      };
      request.onsuccess = function () {
        var database = request.result;
        currentDatabase = database;
        database.onversionchange = function () {
          try { database.close(); } catch (_) {}
          forgetDatabase(database);
        };
        database.onclose = function () { forgetDatabase(database); };
        resolve(database);
      };
      request.onerror = function () { reject(request.error || new Error('Could not open browser upload storage.')); };
    }).catch(function (error) {
      // A temporary storage failure must not poison every later retry.
      databasePromise = null;
      throw error;
    });
    return databasePromise;
  }

  // Only a closed or lost connection is retried. Safari Private Browsing
  // refuses Blob writes with a plain UnknownError ("Error preparing Blob/File
  // data...") and its next transaction never settles, so that one fails at once.
  function lostConnection(error) {
    var name = error && error.name;
    return name === 'InvalidStateError' ||
      (name === 'UnknownError' && /connection|lost/i.test(String(error.message || '')));
  }

  // Runs one storage operation; when the connection was closed or lost under
  // it, reopens the database and runs it once more (unless noRetry).
  async function withDatabase(operation, noRetry) {
    var database = await openDatabase();
    try {
      return await operation(database);
    } catch (error) {
      if (noRetry || !lostConnection(error)) throw error;
      forgetDatabase(database);
      try { database.close(); } catch (_) {}
      return operation(await openDatabase());
    }
  }

  function announceJob(job) {
    if (updateChannel) updateChannel.postMessage({ type: 'changed', id: job.id });
    render();
    if (!stopped && window.document) {
      document.dispatchEvent(new CustomEvent('trc-background-upload:update', { detail: { job: publicJob(job) } }));
    }
  }

  function getJob(id) {
    return withDatabase(function (database) {
      var transaction = database.transaction(JOB_STORE, 'readonly');
      return requestPromise(transaction.objectStore(JOB_STORE).get(id));
    });
  }

  async function getJobs() {
    var jobs = await withDatabase(function (database) {
      var transaction = database.transaction(JOB_STORE, 'readonly');
      return requestPromise(transaction.objectStore(JOB_STORE).getAll());
    });
    return jobs.sort(function (left, right) { return right.createdAt - left.createdAt; });
  }

  async function putJob(job) {
    job.updatedAt = Date.now();
    await withDatabase(function (database) {
      var transaction = database.transaction(JOB_STORE, 'readwrite');
      transaction.objectStore(JOB_STORE).put(job);
      return transactionPromise(transaction);
    });
    announceJob(job);
    return job;
  }

  async function mutateJob(id, mutate) {
    var job = await withDatabase(async function (database) {
      var transaction = database.transaction(JOB_STORE, 'readwrite');
      var store = transaction.objectStore(JOB_STORE);
      var current = await requestPromise(store.get(id));
      if (!current) {
        transaction.abort();
        throw new Error('Upload job was not found.');
      }
      mutate(current);
      current.updatedAt = Date.now();
      store.put(current);
      await transactionPromise(transaction);
      return current;
    });
    announceJob(job);
    return job;
  }

  function storeNewJob(job, files, cardback) {
    return withDatabase(async function (database) {
      var transaction = database.transaction([JOB_STORE, FILE_STORE], 'readwrite');
      var jobs = transaction.objectStore(JOB_STORE);
      var blobs = transaction.objectStore(FILE_STORE);
      var completed = transactionPromise(transaction);
      try {
        jobs.add(job);
        files.forEach(function (file, index) {
          blobs.add({ key: job.id + ':image:' + index, jobId: job.id, kind: 'image', index: index, blob: file });
        });
        if (cardback) {
          blobs.add({ key: job.id + ':cardback', jobId: job.id, kind: 'cardback', index: -1, blob: cardback });
        }
      } catch (error) {
        // A synchronous blob/clone failure must not commit a job with missing files.
        try { transaction.abort(); } catch (_) {}
        await completed.catch(function () {});
        throw error;
      }
      await completed;
    }); // Retried once only for a lost connection (lostConnection).
  }

  async function getStoredBlob(jobId, kind, index) {
    var key = kind === 'cardback' ? jobId + ':cardback' : jobId + ':image:' + index;
    var record = await withDatabase(function (database) {
      var transaction = database.transaction(FILE_STORE, 'readonly');
      return requestPromise(transaction.objectStore(FILE_STORE).get(key));
    });
    return record ? record.blob : null;
  }

  function deleteStoredBlobs(jobId) {
    return withDatabase(async function (database) {
      var transaction = database.transaction(FILE_STORE, 'readwrite');
      var store = transaction.objectStore(FILE_STORE);
      var records = await requestPromise(store.index('jobId').getAllKeys(jobId));
      records.forEach(function (key) { store.delete(key); });
      await transactionPromise(transaction);
    });
  }

  function randomId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    var bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    var value = Array.from(bytes, function (byte) { return byte.toString(16).padStart(2, '0'); }).join('');
    return value.slice(0, 8) + '-' + value.slice(8, 12) + '-' + value.slice(12, 16) + '-' + value.slice(16, 20) + '-' + value.slice(20);
  }

  function randomSecret() {
    var bytes = new Uint8Array(32);
    window.crypto.getRandomValues(bytes);
    var binary = '';
    bytes.forEach(function (byte) { binary += String.fromCharCode(byte); });
    return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  }

  function contentType(file) {
    var type = String(file && file.type || '').split(';')[0].trim().toLowerCase();
    if (type === 'image/jpg') type = 'image/jpeg';
    return type;
  }

  function publicJob(job) {
    return {
      id: job.id,
      status: job.status,
      stage: job.stage,
      progress: job.progress,
      message: job.message,
      error: job.error || '',
      productTitle: job.productTitle,
      quantity: job.quantity,
      createdAt: job.createdAt,
    };
  }

  async function ensureStorageCapacity(files, cardback) {
    if (!navigator.storage) return;
    if (typeof navigator.storage.persist === 'function') {
      try { Promise.resolve(navigator.storage.persist()).catch(function () {}); } catch (_) {}
    }
    if (typeof navigator.storage.estimate !== 'function') return;
    var estimate;
    try { estimate = await navigator.storage.estimate(); } catch (_) { return; }
    if (!estimate || !Number.isFinite(estimate.quota) || !Number.isFinite(estimate.usage)) return;
    var required = files.reduce(function (sum, file) { return sum + Number(file.size || 0); }, 0) +
      Number(cardback && cardback.size || 0);
    var available = Math.max(0, estimate.quota - estimate.usage);
    if (required > available * 0.95) {
      var error = new Error('Insufficient browser storage');
      error.name = 'QuotaExceededError';
      throw error;
    }
  }

  function uploadStorageError(error, bytes) {
    var name = error && error.name || '';
    var size = bytes >= 1024 * 1024 * 1024 ? (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB' : Math.max(1, Math.ceil(bytes / (1024 * 1024))) + ' MB';
    var message;
    if (name === 'QuotaExceededError') message = 'Not enough browser storage for this upload (' + size + '). Try a regular (non-private) window or smaller images.';
    else if (name === 'NotReadableError' || name === 'DataCloneError') message = 'These images could not be read. Keep the original files on this device, then remove and re-add them in the deck editor.';
    else message = 'Browser storage could not save this upload. Try a regular (non-private) window with site storage enabled, or smaller images.';
    var result = new Error(message);
    result.code = error && /^UPLOAD_STORAGE_/.test(String(error.code || '')) ? error.code : 'UPLOAD_STORAGE_' + (name || 'FAILED');
    result.cause = error;
    return result;
  }

  function abandonedBy(input) {
    try { return typeof input.isAbandoned === 'function' && !!input.isAbandoned(); } catch (_) { return false; }
  }
  function abandonedError() {
    var error = new Error('Browser storage did not respond.');
    error.code = 'UPLOAD_STORAGE_TIMEOUT';
    return error;
  }

  // Safari Private Browsing refuses Blob writes, and its connection stays
  // broken after that (the next write never settles). Remember the refusal
  // for this page so later decks fail at once and upload from the page.
  var blobWritesRefused = null;

  async function enqueue(input) {
    input = input || {};
    if (blobWritesRefused) throw uploadStorageError(blobWritesRefused, 0);
    var files = Array.from(input.files || []);
    if (!files.length) throw new Error('Choose at least one card image.');
    if (!input.turnstileToken) throw new Error('Please complete the upload security check.');
    var cardback = input.cardback || null;

    var id = randomId();
    var now = Date.now();
    var date = new Date().toISOString().slice(0, 10);
    var metadata = files.map(function (file, index) {
      return {
        index: index,
        name: String(file.name || ('card-' + (index + 1))),
        type: contentType(file),
        size: Number(file.size || 0),
      };
    });
    var job = {
      id: id,
      schema: 1,
      createdAt: now,
      updatedAt: now,
      status: 'queued',
      stage: 'queued',
      progress: 0,
      message: 'Waiting to upload…',
      error: '',
      workerUrl: input.workerUrl || WORKER_URL,
      variantId: String(input.variantId || ''),
      quantity: Math.max(1, Math.floor(Number(input.quantity || 1)) || 1),
      sellingPlan: input.sellingPlan ? String(input.sellingPlan) : '',
      productTitle: String(input.productTitle || 'Custom deck'),
      productUrl: String(input.productUrl || window.location.pathname),
      properties: Object.assign({}, input.properties || {}),
      adminDeck: input.adminDeck ? {id:String(input.adminDeck.id),secret:String(input.adminDeck.secret)} : null,
      slots: Array.from(input.slots || []),
      slotCount: Number(input.slotCount || (input.slots || []).length || files.length),
      files: metadata,
      cardback: cardback ? {
        name: String(cardback.name || 'card-back.png'),
        type: contentType(cardback),
        size: Number(cardback.size || 0),
      } : null,
      clientSessionRef: date + '/' + id,
      resumeSecret: randomSecret(),
      turnstileToken: String(input.turnstileToken),
      session: '',
      uploadToken: '',
      uploadedIndices: [],
      previewedIndices: [],
      manifestUrl: '',
      validationToken: '',
      cardbackUrl: '',
      cartAdded: false,
    };
    try {
      await ensureStorageCapacity(files, cardback);
      // A late storage estimate: the page has moved on, so write nothing.
      if (abandonedBy(input)) throw abandonedError();
      await storeNewJob(job, files, cardback);
    } catch (error) {
      if (error && error.name === 'UnknownError' && !lostConnection(error)) blobWritesRefused = error;
      if (abandonedBy(input)) throw abandonedError();
      throw uploadStorageError(error, metadata.reduce(function (sum, file) { return sum + file.size; }, 0) + Number(cardback && cardback.size || 0));
    }
    // The page gave up waiting for storage and uploads by itself: never run
    // this copy too.
    if (abandonedBy(input)) {
      await deleteStoredBlobs(job.id).catch(function () {});
      await withDatabase(function (database) {
        var transaction = database.transaction(JOB_STORE, 'readwrite');
        transaction.objectStore(JOB_STORE).delete(job.id);
        return transactionPromise(transaction);
      }).catch(function () {});
      throw abandonedError();
    }
    window.TRCBackgroundUploads._hasActive = true;
    if (updateChannel) updateChannel.postMessage({ type: 'changed', id: job.id });
    render();
    openPanel();
    if (autoStart) scheduleProcessing();
    return publicJob(job);
  }

  // Every request has a deadline: a connection that hangs (a phone switching
  // networks, a captive portal) would otherwise stall the upload forever. The
  // deadline grows with the body, assuming at least ~32 KB/s per request even
  // while several uploads share the connection.
  var FETCH_TIMEOUT_BASE_MS = Number(window.__TRC_BACKGROUND_UPLOADS_TIMEOUT_MS__) > 0
    ? Number(window.__TRC_BACKGROUND_UPLOADS_TIMEOUT_MS__) : 45000;
  var FETCH_TIMEOUT_MAX_MS = 60 * 60 * 1000;
  var MIN_BYTES_PER_SECOND = 32 * 1024;
  var NETWORK_MESSAGE = 'We could not reach the upload service. Check your connection and try again.';
  var TIMEOUT_MESSAGE = 'The upload service took too long to answer. Check your connection and try again.';
  var CART_NETWORK_MESSAGE = 'We could not reach the cart. Check your connection and try again.';
  var OFFLINE_MESSAGE = 'You are offline. The upload will continue when you are back online.';
  var RAW_NETWORK_ERROR = /^(?:load failed|failed to fetch|networkerror when attempting to fetch resource\.?|the operation was aborted\.?|the network connection was lost\.?|network request failed|fetch failed|the internet connection appears to be offline\.?|the request timed out\.?)$/i;

  // Plain copy for raw browser network errors. The shared theme helper wins
  // when it is on the page; this small mapping covers pages without it.
  // Errors from timedFetch already carry plain copy.
  function friendlyMessage(error, fallback) {
    if (error && error.network && error.message) return String(error.message);
    var message = error && typeof error === 'object' ? String(error.message || '') : String(error || '');
    if (window.TRCCompat && typeof window.TRCCompat.friendlyErrorMessage === 'function') {
      try {
        var shared = window.TRCCompat.friendlyErrorMessage(error, fallback || NETWORK_MESSAGE);
        if (shared) return String(shared);
      } catch (_) {}
    }
    var name = error && typeof error === 'object' ? String(error.name || '') : '';
    if (name === 'TimeoutError') return TIMEOUT_MESSAGE;
    if (name === 'AbortError' || RAW_NETWORK_ERROR.test(message.trim())) return fallback || NETWORK_MESSAGE;
    return message || fallback || 'Upload failed. Please try again.';
  }

  function bodyBytes(body) {
    if (!body) return 0;
    if (typeof body === 'string') return body.length;
    if (typeof body.size === 'number') return body.size;
    if (typeof body.byteLength === 'number') return body.byteLength;
    return 0;
  }

  function fetchTimeout(body, parallel) {
    var bytes = bodyBytes(body) * Math.max(1, Number(parallel) || 1);
    return Math.min(FETCH_TIMEOUT_MAX_MS, FETCH_TIMEOUT_BASE_MS + Math.ceil((bytes / MIN_BYTES_PER_SECOND) * 1000));
  }

  function networkError(cause, timedOut, fallback) {
    var offline = !timedOut && navigator.onLine === false;
    var error = new Error(timedOut ? (fallback || TIMEOUT_MESSAGE) : (offline ? OFFLINE_MESSAGE : friendlyMessage(cause, fallback)));
    error.name = timedOut ? 'TimeoutError' : 'NetworkError';
    error.network = true;
    error.timedOut = !!timedOut;
    error.retryable = true;
    if (cause) error.cause = cause;
    return error;
  }

  // fetch() with a deadline that also covers reading the reply. handle(response,
  // timedOut) reads it; HTTP errors it throws (with a status) pass through,
  // network failures and timeouts come back as retryable errors without one.
  function timedFetch(url, init, timeout, handle, fallback) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timedOut = false;
    var timer = controller ? window.setTimeout(function () { timedOut = true; controller.abort(); }, timeout) : null;
    function done() { if (timer !== null) { window.clearTimeout(timer); timer = null; } }
    return Promise.resolve().then(function () {
      return fetch(url, controller ? Object.assign({}, init, { signal: controller.signal }) : init);
    }).then(function (response) {
      return handle(response, function () { return timedOut; });
    }).then(function (value) {
      done();
      return value;
    }, function (error) {
      done();
      if (timedOut) throw networkError(error, true, fallback);
      if (error && (error.status != null || error.network)) throw error;
      var name = error && error.name;
      if (name === 'TypeError' || name === 'AbortError' || RAW_NETWORK_ERROR.test(String(error && error.message || '').trim())) {
        throw networkError(error, false, fallback);
      }
      throw error;
    });
  }

  function request(action, job, options) {
    options = options || {};
    var query = '?type=custom-images&action=' + encodeURIComponent(action);
    if (options.session) query += '&session=' + encodeURIComponent(options.session);
    if (options.index != null) query += '&index=' + encodeURIComponent(options.index);
    return timedFetch((job.workerUrl || WORKER_URL) + query, {
      method: 'POST',
      headers: options.headers || {},
      body: options.body,
      credentials: 'omit',
    }, fetchTimeout(options.body, options.parallel), function (response, timedOut) {
      return response.json().catch(function (readError) {
        // A reply cut off mid-body (or by the deadline) is a network failure,
        // and a garbled success reply is worth one more try: never treat
        // either as an accepted request.
        if (timedOut() || (response.ok && !(readError && readError.name === 'SyntaxError'))) throw readError;
        if (response.ok) throw Object.assign(new Error('Upload service returned an invalid response.'), { retryable: true });
        return { error: 'Upload service returned an invalid response.' };
      }).then(function (body) {
        if (!response.ok) {
          // A failed security check never names the provider to the shopper;
          // the product page retries it once with a fresh token (turnstile).
          var code = String(body.code || '');
          var security = /^turnstile_/.test(code) || (response.status === 401 && /turnstile/i.test(String(body.error || '')));
          var error = new Error(!security ? (body.error || 'Upload failed.')
            : code === 'turnstile_unavailable' ? 'Our security check is not responding. Please try again in a moment.'
            : 'We couldn\u2019t confirm you\u2019re a person.');
          if (security) error.turnstile = code || 'turnstile_invalid';
          error.status = response.status;
          error.retryable = response.status === 408 || response.status === 429 || response.status >= 500;
          throw error;
        }
        return body;
      });
    });
  }

  function delay(milliseconds) {
    return new Promise(function (resolve) { window.setTimeout(resolve, milliseconds); });
  }

  async function withRetry(operation, maximumAttempts) {
    var attempt = 0;
    while (true) {
      try { return await operation(); }
      catch (error) {
        if (attempt >= maximumAttempts - 1 || (error.status != null && !error.retryable)) throw error;
        await delay(500 * Math.pow(2, attempt));
        attempt += 1;
      }
    }
  }

  // One session start per job, across this tab (promise chain) and other
  // tabs (Web Locks). A Turnstile token is single-use, so two concurrent
  // starts for the same job would burn it and fail the second one.
  function withSessionLock(id, operation) {
    var previous = sessionLocks[id] || Promise.resolve();
    var run = previous.catch(function () {}).then(function () {
      if (navigator.locks && typeof navigator.locks.request === 'function') {
        return navigator.locks.request('trc-background-upload-session:' + id, operation);
      }
      return operation();
    });
    var tail = run.catch(function () {});
    sessionLocks[id] = tail;
    tail.then(function () { if (sessionLocks[id] === tail) delete sessionLocks[id]; });
    return run;
  }

  function spentTokenStatus(status) {
    // Any 4xx other than timeout/rate limit means the Worker already ran (or
    // refused) the Turnstile check; that token can never be used again.
    return status >= 400 && status < 500 && status !== 408 && status !== 429;
  }

  async function establishSession(job) {
    return withSessionLock(job.id, async function () {
      var current = await getJob(job.id);
      if (!current) throw new Error('Upload job was not found.');
      if (current.status === 'error') throw new Error(current.error || 'Upload failed. Please try again.');
      return establishSessionUnlocked(current);
    });
  }

  async function establishSessionUnlocked(job) {
    try {
      var resumed = await request('resume', job, {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session: job.clientSessionRef, resumeSecret: job.resumeSecret }),
      });
      job = await mutateJob(job.id, function (current) {
        current.session = resumed.session;
        current.uploadToken = resumed.token;
        current.turnstileToken = '';
      });
      return { job: job, completion: null };
    } catch (resumeError) {
      if (resumeError.status !== 404) throw resumeError;
    }

    if (job.session && job.uploadToken && job.uploadedIndices.length === job.files.length) {
      try {
        var completed = await request('complete', job, {
          session: job.session,
          headers: { 'X-TRC-Upload-Token': job.uploadToken },
        });
        return { job: job, completion: completed };
      } catch (completeError) {
        if (completeError.status !== 404) throw completeError;
      }
    }
    if (job.session) throw await expireSession(job);
    if (!job.turnstileToken) throw needsVerificationError();

    var started;
    try {
      started = await request('start', job, {
        headers: {
          'Content-Type': 'application/json',
          'CF-Turnstile-Response': job.turnstileToken,
        },
        body: JSON.stringify({
          variantId: job.variantId,
          files: job.files.map(function (file) { return { name: file.name, type: file.type, size: file.size }; }),
          slots: job.slots,
          clientSessionRef: job.clientSessionRef,
          resumeSecret: job.resumeSecret,
        }),
      });
    } catch (startError) {
      if (startError.status != null && spentTokenStatus(startError.status)) {
        await mutateJob(job.id, function (current) {
          current.turnstileToken = '';
          current.needsVerification = true;
        }).catch(function () {});
        // Only a 401 is a failed security check. Other refusals (slot count,
        // size, file type, product setup) keep the Worker's reason so the
        // shopper can act on it; the token is spent either way.
        if (startError.status !== 401) {
          startError.tokenSpent = true;
          throw startError;
        }
        var spent = new Error((startError.message || 'Upload failed.') + ' ' + VERIFY_AGAIN_MESSAGE);
        spent.status = startError.status;
        spent.turnstile = startError.turnstile;
        spent.needsVerification = true;
        throw spent;
      }
      throw startError;
    }
    job = await mutateJob(job.id, function (current) {
      current.needsVerification = false;
      current.session = started.session;
      current.uploadToken = started.token;
      current.turnstileToken = '';
    });
    return { job: job, completion: null };
  }

  var VERIFY_AGAIN_MESSAGE = 'Open this deck\'s product page and press Retry there to run the security check again.';
  var SESSION_EXPIRED_MESSAGE = 'This upload was paused for too long and needs to start again. Your images are still saved in this browser, so press Retry to upload them again.';

  // The Worker no longer has this session (it expired before the upload
  // finished). Keep the images saved in this browser and set the job up for a
  // fresh session; Retry asks for a new security check and starts over.
  async function expireSession(job) {
    var expired = job.session;
    await mutateJob(job.id, function (current) {
      // A late reply from an earlier run must not reset a newer session.
      if (current.session !== expired) return;
      current.session = '';
      current.uploadToken = '';
      current.uploadedIndices = [];
      current.previewedIndices = [];
      current.resumeSecret = randomSecret();
      current.clientSessionRef = sessionRefDate() + '/' + current.id;
      current.turnstileToken = '';
      current.needsVerification = true;
    }).catch(function () {});
    var error = new Error(SESSION_EXPIRED_MESSAGE);
    error.needsVerification = true;
    error.sessionExpired = true;
    return error;
  }

  // The Worker slides the session forward after each upload and returns a
  // fresh token when it does; keep using the newest one.
  async function adoptSessionToken(job, result) {
    if (!result || !result.token || result.token === job.uploadToken) return;
    job.uploadToken = result.token;
    await mutateJob(job.id, function (current) { current.uploadToken = result.token; }).catch(function () {});
  }

  // An upload or preview for an open session. A 401 means the token ran out
  // while the session lives on: resume for a fresh token and try once more.
  // A 404 means the session itself is gone.
  async function sessionRequest(action, job, options) {
    function send() {
      return request(action, job, Object.assign({}, options, {
        session: job.session,
        headers: Object.assign({}, options.headers || {}, { 'X-TRC-Upload-Token': job.uploadToken }),
      }));
    }
    var result;
    try {
      result = await withRetry(send, 3);
    } catch (error) {
      if (error.status === 404) throw await expireSession(job);
      if (error.status !== 401 || !job.resumeSecret) throw error;
      var resumed;
      try {
        resumed = await request('resume', job, {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session: job.clientSessionRef, resumeSecret: job.resumeSecret }),
        });
      } catch (resumeError) {
        if (resumeError.status === 404) throw await expireSession(job);
        throw resumeError;
      }
      await adoptSessionToken(job, resumed);
      try {
        result = await withRetry(send, 3);
      } catch (retryError) {
        if (retryError.status === 404) throw await expireSession(job);
        throw retryError;
      }
    }
    await adoptSessionToken(job, result);
    return result;
  }

  function needsVerificationError() {
    var error = new Error('The upload security check expired. ' + VERIFY_AGAIN_MESSAGE);
    error.needsVerification = true;
    return error;
  }

  async function markIndex(jobId, property, index, message, progress) {
    return mutateJob(jobId, function (job) {
      if (job[property].indexOf(index) === -1) job[property].push(index);
      job.message = message;
      job.progress = progress;
      job.stage = property === 'uploadedIndices' ? 'uploading' : 'previews';
    });
  }

  async function uploadOriginals(job) {
    var missing = job.files.map(function (_, index) { return index; }).filter(function (index) {
      return job.uploadedIndices.indexOf(index) === -1;
    });
    if (!missing.length) return getJob(job.id);
    var largest = job.files.reduce(function (value, file) { return Math.max(value, file.size); }, 0);
    var concurrency = largest > 25 * 1024 * 1024 ? 1 : (largest > 10 * 1024 * 1024 ? 2 : 4);
    var cursor = 0;
    async function worker() {
      while (cursor < missing.length) {
        var index = missing[cursor++];
        var file = await getStoredBlob(job.id, 'image', index);
        if (!file) throw new Error('A saved card image is missing from this browser.');
        await sessionRequest('upload', job, {
          index: index,
          headers: { 'Content-Type': job.files[index].type },
          body: file,
          parallel: concurrency,
        });
        var current = await getJob(job.id);
        var completed = current.uploadedIndices.length + (current.uploadedIndices.indexOf(index) === -1 ? 1 : 0);
        await markIndex(job.id, 'uploadedIndices', index,
          'Uploading cards: ' + completed + ' of ' + job.files.length,
          Math.round((completed / job.files.length) * 80));
      }
    }
    await Promise.all(Array.from({ length: Math.min(concurrency, missing.length) }, worker));
    return getJob(job.id);
  }

  async function makePreview(blob) {
    var bitmap = null;
    var image = null;
    var objectUrl = '';
    try {
      if (typeof window.createImageBitmap === 'function') {
        bitmap = await window.createImageBitmap(blob);
      } else {
        objectUrl = URL.createObjectURL(blob);
        image = await new Promise(function (resolve, reject) {
          var element = new Image();
          element.onload = function () { resolve(element); };
          element.onerror = function () { reject(new Error('A card image could not be decoded for its cart preview.')); };
          element.src = objectUrl;
        });
      }
      var sourceWidth = bitmap ? bitmap.width : image.naturalWidth;
      var sourceHeight = bitmap ? bitmap.height : image.naturalHeight;
      var scale = Math.min(1, 480 / sourceWidth, 672 / sourceHeight);
      var canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(sourceWidth * scale));
      canvas.height = Math.max(1, Math.round(sourceHeight * scale));
      var context = canvas.getContext('2d', { alpha: false });
      if (!context) throw new Error('This browser could not prepare the cart preview.');
      context.fillStyle = '#fff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(bitmap || image, 0, 0, canvas.width, canvas.height);
      return await new Promise(function (resolve, reject) {
        canvas.toBlob(function (preview) {
          if (!preview) reject(new Error('This browser could not prepare the cart preview.'));
          else if (preview.size > 512 * 1024) reject(new Error('A cart preview was unexpectedly large.'));
          else resolve(preview);
        }, 'image/jpeg', 0.72);
      });
    } finally {
      if (bitmap && typeof bitmap.close === 'function') bitmap.close();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    }
  }

  async function uploadPreviews(job) {
    var missing = job.files.map(function (_, index) { return index; }).filter(function (index) {
      return job.previewedIndices.indexOf(index) === -1;
    });
    if (!missing.length) return getJob(job.id);
    var cursor = 0;
    async function worker() {
      while (cursor < missing.length) {
        var index = missing[cursor++];
        var file = await getStoredBlob(job.id, 'image', index);
        if (!file) throw new Error('A saved card image is missing from this browser.');
        var preview = await makePreview(file);
        await sessionRequest('preview', job, {
          index: index,
          headers: { 'Content-Type': 'image/jpeg' },
          body: preview,
        });
        var current = await getJob(job.id);
        var completed = current.previewedIndices.length + (current.previewedIndices.indexOf(index) === -1 ? 1 : 0);
        await markIndex(job.id, 'previewedIndices', index,
          'Preparing previews: ' + completed + ' of ' + job.files.length,
          80 + Math.round((completed / job.files.length) * 15));
      }
    }
    await Promise.all(Array.from({ length: Math.min(4, missing.length) }, worker));
    return getJob(job.id);
  }

  async function finalize(job, knownCompletion) {
    await mutateJob(job.id, function (current) {
      current.status = 'finalizing';
      current.stage = 'finalizing';
      current.progress = 96;
      current.message = 'Finalizing upload…';
    });
    var result = knownCompletion;
    if (!result) {
      try {
        result = await withRetry(function () {
          return request('complete', job, {
            session: job.session,
            headers: { 'X-TRC-Upload-Token': job.uploadToken },
          });
        }, 3);
      } catch (error) {
        if (error.status === 404) throw await expireSession(job);
        throw error;
      }
    }
    return mutateJob(job.id, function (current) {
      current.manifestUrl = result.manifestUrl;
      current.validationToken = result.validationToken;
      current.progress = current.cardback ? 97 : 98;
      current.message = current.cardback ? 'Uploading custom card back…' : 'Adding to cart…';
    });
  }

  async function uploadCardback(job) {
    if (!job.cardback || job.cardbackUrl) return job;
    var blob = await getStoredBlob(job.id, 'cardback');
    if (!blob) throw new Error('The saved custom card back is missing from this browser.');
    var result = await withRetry(function () {
      return request('cardback', job, {
        session: job.session,
        headers: { 'Content-Type': job.cardback.type, 'X-TRC-Upload-Token': job.uploadToken },
        body: blob,
      });
    }, 3);
    return mutateJob(job.id, function (current) {
      current.cardbackUrl = result.url;
      current.validationToken = result.validationToken;
      current.progress = 98;
      current.message = 'Adding to cart…';
    });
  }


  function cartJson() {
    return timedFetch('/cart.js', { headers: { Accept: 'application/json' } }, FETCH_TIMEOUT_BASE_MS, function (response) {
      if (!response.ok) throw Object.assign(new Error('Could not check the cart.'), { status: response.status });
      return response.json();
    }, CART_NETWORK_MESSAGE);
  }

  function publishCart(cart) {
    if (window.FoxTheme && FoxTheme.pubsub) {
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cart });
    }
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
  }

  async function addToCart(job) {
    var existingCart = job.adminDeck ? {items:[]} : await cartJson();
    var duplicate = (existingCart.items || []).some(function (item) {
      return item.properties && item.properties['_Upload job ID'] === job.id;
    });
    if (duplicate) {
      publishCart(existingCart);
      return existingCart;
    }
    var properties = Object.assign({}, job.properties || {});
    delete properties['_Custom images'];
    delete properties['_Deck validation'];
    delete properties['_Deck snapshot'];
    delete properties['Card back'];
    properties['_Input method'] = 'Custom image upload';
    properties['_Custom images'] = job.slotCount + ' custom cards|' + job.manifestUrl;
    properties['_Deck validation'] = job.validationToken;
    properties['_Upload job ID'] = job.id;
    if (job.cardback && job.cardbackUrl) {
      properties['Card back'] = job.cardback.name.replace(/\|/g, '-') + '|' + job.cardbackUrl;
    }

    if (job.adminDeck) {
      var draftBody = JSON.stringify({id:job.adminDeck.id,secret:job.adminDeck.secret,item:{id:String(job.variantId),quantity:job.quantity,properties:properties}});
      return timedFetch('https://studio-editor.tablereadycards.com/draft-decks/submit', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body:draftBody,
      }, fetchTimeout(draftBody), function (response) {
        return response.json().catch(function () { return {}; }).then(function (draftResult) {
          if (!response.ok) throw Object.assign(new Error(draftResult.error || 'Could not send deck to TRC Studio.'), { status: response.status });
          return draftResult;
        });
      });
    }

    var sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', {
      bubbles: true,
      detail: { sections: sections },
    }));
    var item = {
      id: job.variantId,
      quantity: job.quantity,
      properties: properties,
    };
    if (job.sellingPlan) item.selling_plan = job.sellingPlan;
    var payload = { items: [item] };
    if (sections.length) {
      payload.sections = sections;
      payload.sections_url = window.location.pathname;
    }
    // Not retried here: an add whose reply was lost may still have landed, and
    // the '_Upload job ID' check above finds it on the next attempt.
    var cartBody = JSON.stringify(payload);
    var result = await timedFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: cartBody,
    }, fetchTimeout(cartBody), function (response) {
      return response.json().catch(function () { return {}; }).then(function (added) {
        if (!response.ok || added.status) {
          throw Object.assign(new Error(added.description || added.message || 'Shopify could not add this deck to the cart.'), { status: response.status || 422 });
        }
        return added;
      });
    }, CART_NETWORK_MESSAGE);
    var updatedCart = await cartJson();
    updatedCart.sections = result.sections || {};
    publishCart(updatedCart);
    document.dispatchEvent(new CustomEvent('trc-background-upload:added', { detail: { jobId: job.id, cart: updatedCart } }));
    return updatedCart;
  }

  async function processJob(id) {
    var job = await getJob(id);
    if (!job || job.status === 'completed' || job.status === 'error') return;
    try {
      job = await mutateJob(id, function (current) {
        current.status = 'uploading';
        current.stage = 'preparing';
        current.error = '';
        current.message = current.session ? 'Resuming secure upload…' : 'Preparing secure upload…';
      });
      // An upload that already finished (only its add-to-cart failed) goes
      // straight to the card back and the cart. Its images are not uploaded
      // again and its session is not needed (U-4).
      if (!(job.manifestUrl && job.validationToken)) {
        var established = await establishSession(job);
        job = established.job;
        if (!established.completion) {
          job = await uploadOriginals(job);
          job = await uploadPreviews(job);
        }
        job = await finalize(job, established.completion);
      }
      job = await uploadCardback(job);
      await mutateJob(id, function (current) {
        current.status = 'adding';
        current.stage = 'adding';
        current.progress = 99;
        current.message = current.adminDeck ? 'Sending to TRC Studio…' : 'Adding to cart…';
      });
      job = await getJob(id);
      await addToCart(job);
      await mutateJob(id, function (current) {
        current.status = 'completed';
        current.stage = 'completed';
        current.progress = 100;
        current.message = current.adminDeck ? 'Ready to save in TRC Studio' : 'Added to cart';
        current.cartAdded = !current.adminDeck;
        if (current.adminDeck) current.adminDeck.secret = '';
        current.error = '';
        current.resumeSecret = '';
        current.turnstileToken = '';
        current.uploadToken = '';
      });
      await deleteStoredBlobs(id);
    } catch (error) {
      var interruptedByNavigation = document.visibilityState === 'hidden' &&
        (error && (error.name === 'AbortError' || error.status == null));
      // A dropped connection or a timeout resumes by itself once the page is
      // visible and online again (a few times at most); the shopper can also
      // press Retry.
      var connectionLost = !!(error && error.network && !error.needsVerification);
      await mutateJob(id, function (current) {
        current.status = interruptedByNavigation ? 'queued' : 'error';
        current.stage = interruptedByNavigation ? 'queued' : 'error';
        current.message = interruptedByNavigation ? 'Resuming on the next page…' : 'Upload needs attention';
        current.error = interruptedByNavigation ? '' : friendlyMessage(error, 'Upload failed. Please try again.');
        current.resumeOnReconnect = !interruptedByNavigation && connectionLost;
      }).catch(function () {});
    }
  }

  function nextPendingJob(jobs) {
    // Oldest first. A job is attempted at most once per run so a job that is
    // re-queued while the page is being hidden cannot spin this loop.
    for (var index = jobs.length - 1; index >= 0; index--) {
      var job = jobs[index];
      if (ACTIVE_STATUSES.indexOf(job.status) !== -1 && !attemptedThisRun[job.id]) return job;
    }
    return null;
  }

  async function processPendingJobs() {
    if (processing || stopped) return;
    processing = true;
    attemptedThisRun = Object.create(null);
    try {
      // Re-scan after every job: decks queued while another deck uploads must
      // start in this tab instead of waiting for the next page load.
      while (!stopped) {
        processingRequested = false;
        var next = nextPendingJob(await getJobs());
        if (!next) {
          if (processingRequested) continue;
          break;
        }
        attemptedThisRun[next.id] = true;
        await processJob(next.id);
      }
    } finally {
      processing = false;
      render();
    }
  }

  function scheduleProcessing() {
    if (stopped) return;
    processingRequested = true;
    window.setTimeout(function () {
      if (stopped) return;
      if (navigator.locks && typeof navigator.locks.request === 'function') {
        navigator.locks.request('trc-background-upload-worker', { ifAvailable: true }, function (lock) {
          if (!lock) return;
          return processPendingJobs();
        }).catch(function () {});
      } else {
        processPendingJobs().catch(function () {});
      }
    }, 0);
  }

  function sessionRefDate() {
    return new Date().toISOString().slice(0, 10);
  }

  async function retry(id) {
    var existing = await getJob(id);
    if (!existing) return;
    var freshToken = '';
    if (!existing.session && !existing.turnstileToken) {
      // The original single-use security token was spent. Only a page that
      // can show the security check (the product page) can supply a new one.
      if (!tokenProvider) {
        await mutateJob(id, function (job) {
          job.status = 'error';
          job.stage = 'error';
          job.needsVerification = true;
          job.message = 'Upload needs attention';
          job.error = 'The upload security check expired. ' + VERIFY_AGAIN_MESSAGE;
        });
        return;
      }
      try {
        freshToken = String(await tokenProvider() || '');
        if (!freshToken) throw new Error('Please complete the upload security check.');
      } catch (error) {
        await mutateJob(id, function (job) {
          job.status = 'error';
          job.stage = 'error';
          job.message = 'Upload needs attention';
          job.error = error && error.message ? error.message : 'Please complete the upload security check.';
        });
        return;
      }
    }
    delete attemptedThisRun[id];
    await mutateJob(id, function (job) {
      if (freshToken && !job.session) {
        job.turnstileToken = freshToken;
        job.needsVerification = false;
        // A fresh start must carry today's session date; the Worker rejects
        // background identities from an earlier UTC day.
        job.clientSessionRef = sessionRefDate() + '/' + job.id;
      }
      job.status = 'queued';
      job.stage = 'queued';
      job.error = '';
      job.message = 'Waiting to retry…';
      job.resumeOnReconnect = false;
    });
    scheduleProcessing();
  }

  // Back online, or back on this tab: continue queued jobs and give jobs that
  // stopped on a dropped connection another go. Jobs that need a new security
  // check are left for the shopper. Several tabs may do this at once; the
  // status check inside the write and the worker lock keep it to one run.
  var MAX_AUTO_RESUMES = 5;
  var resumeTimer = null;
  function resumeAfterReconnect() {
    if (stopped || !autoStart) return;
    if (resumeTimer !== null) return;
    resumeTimer = window.setTimeout(function () {
      resumeTimer = null;
      if (stopped || navigator.onLine === false) return;
      if (document.visibilityState === 'hidden') return;
      getJobs().then(function (jobs) {
        jobs.forEach(function (job) { if (job.status === 'queued') delete attemptedThisRun[job.id]; });
        var waiting = jobs.filter(function (job) {
          return job.status === 'error' && job.resumeOnReconnect && !job.needsVerification &&
            (job.autoResumes || 0) < MAX_AUTO_RESUMES &&
            !!(job.session || job.turnstileToken || (job.manifestUrl && job.validationToken));
        });
        return Promise.all(waiting.map(function (job) {
          delete attemptedThisRun[job.id];
          return mutateJob(job.id, function (current) {
            if (current.status !== 'error' || !current.resumeOnReconnect) return;
            current.resumeOnReconnect = false;
            current.autoResumes = (current.autoResumes || 0) + 1;
            current.status = 'queued';
            current.stage = 'queued';
            current.error = '';
            current.message = 'Resuming upload…';
          }).catch(function () {});
        }));
      }).catch(function () {}).then(function () {
        refreshActiveFlag();
        scheduleProcessing();
      });
    }, 250);
  }

  // Establish the upload session for a freshly queued job right away, while
  // its single-use token is fresh, so the product page can keep the deck in
  // the editor until the upload has really started.
  async function start(id) {
    var job = await getJob(id);
    if (!job) throw new Error('Upload job was not found.');
    if (job.session || job.status === 'completed') return publicJob(job);
    if (job.status === 'error') throw new Error(job.error || 'Upload failed. Please try again.');
    try {
      var established = await establishSession(job);
      return publicJob(established.job);
    } catch (error) {
      var message = friendlyMessage(error, 'Upload failed. Please try again.');
      await mutateJob(id, function (current) {
        if (current.session || current.status === 'completed') return;
        current.status = 'error';
        current.stage = 'error';
        current.message = 'Upload needs attention';
        current.error = message;
      }).catch(function () {});
      throw error;
    }
  }

  function setTokenProvider(provider) {
    if (typeof provider !== 'function') return function () {};
    tokenProvider = provider;
    return function () { if (tokenProvider === provider) tokenProvider = null; };
  }

  function sameOriginPath(value) {
    var path = String(value || '');
    return /^\/(?!\/)[^\s"'<>\\]*$/.test(path) ? path : '';
  }

  async function dismiss(id) {
    var job = await getJob(id);
    if (!job || ACTIVE_STATUSES.indexOf(job.status) !== -1) return;
    await deleteStoredBlobs(id).catch(function () {});
    await withDatabase(function (database) {
      var transaction = database.transaction(JOB_STORE, 'readwrite');
      transaction.objectStore(JOB_STORE).delete(id);
      return transactionPromise(transaction);
    });
    if (updateChannel) updateChannel.postMessage({ type: 'changed', id: id });
    render();
  }

  function uploadCenter() {
    if (stopped || !window.document || !document.documentElement) return null;
    return document.querySelector('[data-trc-upload-center]');
  }

  function openPanel() {
    var center = uploadCenter();
    if (!center) return;
    var panel = center.querySelector('[data-trc-upload-panel]');
    var button = center.querySelector('[data-trc-upload-toggle]');
    if (panel) panel.hidden = false;
    if (button) button.setAttribute('aria-expanded', 'true');
  }

  function closePanel() {
    var center = uploadCenter();
    if (!center) return;
    var panel = center.querySelector('[data-trc-upload-panel]');
    var button = center.querySelector('[data-trc-upload-toggle]');
    if (panel) panel.hidden = true;
    if (button) button.setAttribute('aria-expanded', 'false');
  }

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>'"]/g, function (character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character];
    });
  }

  async function render() {
    if (stopped || !window.document) return;
    var center = uploadCenter();
    if (!center) return;
    var jobs;
    try { jobs = await getJobs(); } catch (_) { jobs = []; }
    center.hidden = jobs.length === 0;
    if (!jobs.length) return;
    var active = jobs.filter(function (job) { return ACTIVE_STATUSES.indexOf(job.status) !== -1; });
    var errors = jobs.filter(function (job) { return job.status === 'error'; });
    var overall = active.length
      ? Math.round(active.reduce(function (sum, job) { return sum + job.progress; }, 0) / active.length)
      : (errors.length ? 0 : 100);
    center.style.setProperty('--trc-upload-progress', overall);
    center.classList.toggle('has-error', errors.length > 0);
    center.classList.toggle('is-complete', !active.length && !errors.length);
    var percent = center.querySelector('[data-trc-upload-percent]');
    if (percent) percent.textContent = errors.length ? '!' : (!active.length ? '✓' : overall + '%');
    var toggle = center.querySelector('[data-trc-upload-toggle]');
    if (toggle) toggle.setAttribute('aria-label', errors.length
      ? 'An upload needs attention'
      : (active.length ? overall + '% uploaded. View upload progress.' : 'Uploads added to cart'));
    var list = center.querySelector('[data-trc-upload-list]');
    if (!list) return;
    list.innerHTML = jobs.map(function (job) {
      var action = '';
      if (job.status === 'error') {
        var verifyPath = job.needsVerification && !tokenProvider ? sameOriginPath(job.productUrl) : '';
        action = (verifyPath ? '<a href="' + escapeHtml(verifyPath) + '">Open deck page</a>' : '') +
          '<button type="button" data-trc-upload-retry="' + escapeHtml(job.id) + '">Retry</button>' +
          '<button type="button" class="is-secondary" data-trc-upload-dismiss="' + escapeHtml(job.id) + '">Dismiss</button>';
      } else if (job.status === 'completed') {
        action = '<a href="/cart">View cart</a><button type="button" class="is-secondary" data-trc-upload-dismiss="' + escapeHtml(job.id) + '">Done</button>';
      }
      return '<article class="trc-upload-job is-' + escapeHtml(job.status) + '">' +
        '<div class="trc-upload-job__head"><strong>' + escapeHtml(job.productTitle) + '</strong><span>' + job.progress + '%</span></div>' +
        '<div class="trc-upload-job__track"><span style="width:' + job.progress + '%"></span></div>' +
        '<p>' + escapeHtml(job.error || job.message) + '</p>' +
        (action ? '<div class="trc-upload-job__actions">' + action + '</div>' : '') +
        '</article>';
    }).join('');
  }

  function hasActiveUploads(jobs) {
    return jobs.some(function (job) { return ACTIVE_STATUSES.indexOf(job.status) !== -1; });
  }

  function bindUi() {
    document.addEventListener('click', function (event) {
      var toggle = event.target.closest('[data-trc-upload-toggle]');
      if (toggle) {
        event.preventDefault();
        var panel = toggle.closest('[data-trc-upload-center]').querySelector('[data-trc-upload-panel]');
        if (panel.hidden) openPanel(); else closePanel();
        return;
      }
      var close = event.target.closest('[data-trc-upload-close]');
      if (close) { closePanel(); return; }
      var retryButton = event.target.closest('[data-trc-upload-retry]');
      if (retryButton) { retry(retryButton.getAttribute('data-trc-upload-retry')); return; }
      var dismissButton = event.target.closest('[data-trc-upload-dismiss]');
      if (dismissButton) { dismiss(dismissButton.getAttribute('data-trc-upload-dismiss')); return; }
      var center = uploadCenter();
      if (center && !center.contains(event.target)) closePanel();

      var link = event.target.closest('a[href]');
      if (link && !link.target && !link.hasAttribute('download')) {
        try {
          if (new URL(link.href, window.location.href).origin === window.location.origin) {
            internalNavigationUntil = Date.now() + 5000;
          }
        } catch (_) {}
      }
    });
    document.addEventListener('submit', function (event) {
      if (event.defaultPrevented) return;
      try {
        var action = new URL(event.target.action || window.location.href, window.location.href);
        if (action.origin === window.location.origin) internalNavigationUntil = Date.now() + 2000;
      } catch (_) {}
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closePanel();
    });
    window.addEventListener('pageshow', function () { internalNavigationUntil = 0; });
    window.addEventListener('beforeunload', function (event) {
      if (Date.now() < internalNavigationUntil) return;
      if (!window.TRCBackgroundUploads || !window.TRCBackgroundUploads._hasActive) return;
      event.preventDefault();
      event.returnValue = '';
    });
  }

  async function refreshActiveFlag() {
    if (stopped) return;
    try { window.TRCBackgroundUploads._hasActive = hasActiveUploads(await getJobs()); }
    catch (_) { window.TRCBackgroundUploads._hasActive = false; }
  }

  var ready = openDatabase().then(function () {
    return refreshActiveFlag().then(function () {
      render();
      if (autoStart) scheduleProcessing();
    });
  }).catch(function (error) {
    console.warn('[TRC uploads]', error);
  });

  document.addEventListener('trc-background-upload:update', refreshActiveFlag);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') resumeAfterReconnect();
  });
  window.addEventListener('online', resumeAfterReconnect);
  document.addEventListener('shopify:section:load', render);
  if (updateChannel) {
    updateChannel.addEventListener('message', function () {
      refreshActiveFlag();
      render();
      if (autoStart) scheduleProcessing();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindUi, { once: true });
  else bindUi();

  window.TRCBackgroundUploads = {
    ready: ready,
    enqueue: enqueue,
    start: start,
    setTokenProvider: setTokenProvider,
    retry: retry,
    dismiss: dismiss,
    getJobs: function () { return ready.then(getJobs); },
    open: openPanel,
    _shutdown: function () {
      stopped = true;
      if (resumeTimer !== null) { window.clearTimeout(resumeTimer); resumeTimer = null; }
      if (updateChannel) updateChannel.close();
      updateChannel = null;
    },
    _hasActive: false,
  };
})();
